import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';

import ArrowLeft from '@/assets/icons/arrowLeft';
import { Screen } from '@/components/ScreenComponent';
import { ScreenNavBar } from '@/components/ScreenNavBar';
import { SizedBox } from '@/components/SizedBox';
import { Text } from '@/components/TextComponent';
import { Column } from '@/components/lib/components';
import { MaxWidthWrapper } from '@/feature/responsive-design/WebComponents';
import { productNames } from '@/hooks/use-active-product-name';
import { useActiveWallet, useSwitchActiveWallet } from '@/hooks/use-active-wallet';
import { useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { useWallet } from '@/hooks/use-wallet';
import { AccountStackParamList } from '@/navigation/types';
import { LoadingScreen } from '@/screens/LoadingScreen';

import { WalletCards } from '../components/wallet-card/WalletCards';
import { buildCardItems, getCurrenciesForProduct } from '../components/wallet-card/utils';

export type AccountRouteProp = RouteProp<AccountStackParamList, 'SecondaryWallet'>;

export const SecondaryWallet = () => {
    const { t } = useTranslation(['account', 'wallet']);
    const { params } = useRoute<AccountRouteProp>();
    const navigation = useNavigation();
    const activeProduct = useJurisdictionStore(state => state.product);
    const switchActiveWallet = useSwitchActiveWallet();
    const { wallets } = useActiveWallet();
    const { walletLoading, wallet } = useWallet();

    const productName = productNames[params.product];

    const cards = useMemo(() => {
        const currencies = getCurrenciesForProduct(params.product);
        return buildCardItems(currencies, wallets[params.product], params.product);
    }, [params.product, wallets]);

    const handleOnClose = () => {
        switchActiveWallet(activeProduct);
        navigation.goBack();
    };

    if (walletLoading && !wallet) {
        return <LoadingScreen />;
    }

    return (
        <Screen>
            <MaxWidthWrapper>
                <ScreenNavBar
                    title={
                        params.product === 'sweeps'
                            ? t('wallet:social_wallet')
                            : t('product_wallet', { product: productName })
                    }
                    closeIconMode={'back'}
                    onClose={handleOnClose}
                    height={48}
                    footer={<SizedBox value={22} />}
                    backIcon={<ArrowLeft />}
                />
                <Column>
                    <WalletCards cards={cards} />
                </Column>
                <Column paddingHorizontal={'s16'}>
                    <Text variant="bodySmall" color="neutral300">
                        {t('exclusive_usage_description', {
                            product: productName,
                            mode:
                                params.product === 'pickem' || params.product === 'arcade'
                                    ? t('fantasy_picks_and_arcade_modes')
                                    : t('product_mode', {
                                          product: productName,
                                      }),
                            accessProduct:
                                params.product === 'pickem' || params.product === 'arcade'
                                    ? t('picks_or_arcade')
                                    : productName,
                        })}
                    </Text>
                </Column>
            </MaxWidthWrapper>
        </Screen>
    );
};
